import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "tile-content" }

import { computed } from "vue";
import { useQuery } from "@vue/apollo-composable";

import { ProjectRoutes } from "@/router/projectRoutes";
import ButtonIcon from "@/components/ButtonIcon.vue";
import TileDashboard from "@/components/TileDashboard.vue";

import { GET_USER } from "@/graphql/user/queries";
import { GET_PHASES } from "@/graphql/orcha/phases/queries";
import { GET_ACTIVITY_STATUSES } from "@/graphql/orcha/activities/queries";
import { GET_PROJECT_USERS } from "@/graphql/project/queries";
import { GET_ACTIVITY_TIME_SPENT } from "@/graphql/timesheets/queries";

import { AoProjectPhases } from "@26lights/orcha";

import useMutationHandlers from "@/composables/useMutationHandlers";

import { isEmployee } from "@/helpers/router/queries";
import { ProjectAccessRight } from "@/helpers/typesDefinition";
import { User } from "@/helpers/user/typesDefinition";


export default /*@__PURE__*/_defineComponent({
  __name: 'TileProjectDashboardPhases',
  props: {
    id: {}
  },
  setup(__props: any) {

const props = __props;

const { createErrorHandler } = useMutationHandlers();

/*
 * Current user
 */
const { result: userResult, onError: onUserError } = useQuery(GET_USER);

onUserError(createErrorHandler("getUser"));

const isUserEmployee = computed(() =>
  isEmployee(userResult.value?.user?.accessRights),
);

/*
 * Project users
 *
 * IMPORTANT:
 * useQuery must be called directly during setup,
 * never inside a computed().
 *
 * We use Apollo's enabled option instead.
 */
const { result: projectUsersResult, onError: onUsersError } = useQuery(
  GET_PROJECT_USERS,
  () => ({
    projectId: props.id,
  }),
  () => ({
    enabled: isUserEmployee.value,
  }),
);

onUsersError(createErrorHandler("getProjectUsers"));

const users = computed(() => {
  if (!isUserEmployee.value) {
    return [];
  }

  return (projectUsersResult.value?.project?.accessRights?.items ?? [])
    .map((ar: ProjectAccessRight) => ar.user)
    .filter((user: User) => user.firstName && user.lastName);
});

/*
 * Phases
 *
 * IMPORTANT:
 * Same thing here: query is created once during setup.
 */
const { result: phasesResult, onError: onPhasesError } = useQuery(
  GET_PHASES,
  () => ({
    projectId: props.id,
    includeInactive: false,
  }),
);

onPhasesError(createErrorHandler("getPhases"));

const phases = computed(() => phasesResult.value?.phases ?? []);

/*
 * Activity statuses
 */
const { result: activityStatusResult, onError: onActivityStatusesError } =
  useQuery(GET_ACTIVITY_STATUSES);

onActivityStatusesError(createErrorHandler("getActivityStatuses"));

const activityStatus = computed(
  () => activityStatusResult.value?.activityStatuses ?? [],
);

const closedStatus = computed(() => {
  if (!activityStatus.value.length) {
    return undefined;
  }

  return activityStatus.value[activityStatus.value.length - 1];
});

/*
 * Collect activity IDs from phases for time tracking.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const activityIdsForQuery = computed((): string[] | undefined => {
  const ids: string[] = [];

  for (const phase of phases.value) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const milestone of (phase as any).milestones ?? []) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const activity of (milestone as any).activities ?? []) {
        if (activity.id) {
          ids.push(activity.id);
        }
      }
    }
  }

  return ids.length > 0 ? ids : undefined;
});

/*
 * Activity time tracking
 */
const { result: activityTimeSpentResult } = useQuery(
  GET_ACTIVITY_TIME_SPENT,
  () => ({
    activityIds: activityIdsForQuery.value,
  }),
  () => ({
    enabled: Boolean(activityIdsForQuery.value),
  }),
);

const timeTrackingData = computed(() => {
  const result = activityTimeSpentResult.value?.activityTimeSpent?.data ?? [];

  const map: Record<
    string,
    {
      timeSpent: number;
    }
  > = {};

  result.forEach((item: { activityId: string; timeSpent: number }) => {
    map[item.activityId] = {
      timeSpent: item.timeSpent,
    };
  });

  return map;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(TileDashboard, {
    title: _ctx.$t('dashboard.title.phases')
  }, {
    actions: _withCtx(() => [
      _createVNode(ButtonIcon, {
        "icon-name": "material-symbols:apps",
        label: _ctx.$t('buttons.seeDetails'),
        "icon-position": "start",
        variant: "outline",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (
          _ctx.$router.push({
            name: _unref(ProjectRoutes).PROJECT_WORK_PLAN,
          })
        ))
      }, null, 8, ["label"])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_unref(AoProjectPhases), {
          phases: phases.value,
          "closed-status": closedStatus.value,
          "is-editable": false,
          users: users.value,
          readonly: true,
          "time-tracking-data": timeTrackingData.value
        }, null, 8, ["phases", "closed-status", "users", "time-tracking-data"])
      ])
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})